import { jsPDF } from "jspdf";
import { loadSignatureFont } from "./signatureFont";

export const CUR = {
  USD: { total: "TOTAL PRICE U.S.$'S", sym: "$" },
  CAD: { total: "TOTAL PRICE CDN$'S", sym: "$" },
  EUR: { total: "TOTAL PRICE €'S", sym: "€" },
  GBP: { total: "TOTAL PRICE £'S", sym: "£" },
};

export const lineTotal = (it) => {
  const u = Number(it.unit);
  if (!u) return null;
  const q = parseFloat(it.qty);
  return (Number.isFinite(q) && q > 0 ? q : 1) * u;
};

export async function buildInvoiceDoc(m, items) {
  const subtotal = items.reduce((s, it) => s + (lineTotal(it) || 0), 0);
  const total = subtotal + (Number(m.handling) || 0) + (Number(m.freight) || 0) + (Number(m.misc) || 0);
  const sym = CUR[m.currency].sym;
  const fmt = (n) => `${sym}${(Number(n) || 0).toFixed(2)}`;

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const sigFont = await loadSignatureFont(doc);
  const ML = 10, MRX = 200;
  const MID = 104, RMID = 152;
  doc.setDrawColor(40); doc.setLineWidth(0.25);
  const rect = (x, y, w, h) => doc.rect(x, y, w, h);
  const lbl = (t, x, y) => { doc.setFont("helvetica", "bold"); doc.setFontSize(5.8); doc.setTextColor(70); doc.text(String(t).toUpperCase(), x + 1.5, y + 3); doc.setTextColor(0); };
  const val = (t, x, y, maxW, size = 9.5) => {
    doc.setFont("helvetica", "bold"); doc.setFontSize(size); doc.setTextColor(55);
    const lines = doc.splitTextToSize(String(t || ""), maxW);
    doc.text(lines, x + 2, y);
    doc.setTextColor(0);
    return lines.length;
  };

  // ---- upper grid (page 1) ----
  const gTop = 20;
  rect(ML, gTop, MID - ML, 26);         lbl("Shipper/Exporter", ML, gTop);            val(m.shipper, ML, gTop + 8, MID - ML - 5);
  rect(ML, gTop + 26, MID - ML, 26);    lbl("Consignee", ML, gTop + 26);              val(m.consignee, ML, gTop + 34, MID - ML - 5);
  rect(ML, gTop + 52, MID - ML, 26);    lbl("Notify Party/ Intermediate Consignee", ML, gTop + 52); val(m.notifyParty, ML, gTop + 60, MID - ML - 5);
  const leftBottom = gTop + 78;
  const rows = [
    [["Date", m.date], ["Commercial Invoice No.", m.invoiceNo]],
    [["Customer P.O. Number", m.customerPo], ["Date of Export", m.dateExport]],
    [["Country of Origin", m.origin], ["B/L / AWB Number", m.blAwb]],
    [["Final Destination", m.finalDest], ["Export Route / Carrier", m.exportRoute]],
    [["Terms of Sale", m.termsSale], ["Terms of Payment", m.termsPayment]],
  ];
  const rh = 9;
  let ry = gTop;
  rows.forEach(([l, r]) => {
    rect(MID, ry, RMID - MID, rh); lbl(l[0], MID, ry); val(l[1], MID, ry + 7, RMID - MID - 4, 9);
    rect(RMID, ry, MRX - RMID, rh); lbl(r[0], RMID, ry); val(r[1], RMID, ry + 7, MRX - RMID - 4, 9);
    ry += rh;
  });
  rect(MID, ry, MRX - MID, rh);
  lbl("Freight:", MID, ry);
  const cb = (x, checked, text) => {
    doc.rect(x, ry + 3.2, 4, 4);
    if (checked) {
      doc.setLineWidth(0.5);
      doc.line(x + 0.8, ry + 5.2, x + 1.8, ry + 6.4);
      doc.line(x + 1.8, ry + 6.4, x + 3.4, ry + 3.8);
      doc.setLineWidth(0.25);
    }
    doc.setFont("helvetica", "bold"); doc.setFontSize(6); doc.setTextColor(70);
    doc.text(text, x + 6, ry + 6.4); doc.setTextColor(0);
  };
  cb(MID + 20, m.freightTerms === "prepaid", "PREPAID");
  cb(MID + 55, m.freightTerms === "collect", "COLLECT");
  ry += rh;
  rect(MID, ry, MRX - MID, leftBottom - ry); lbl("Marks:", MID, ry); val(m.marks, MID, ry + 7, MRX - MID - 4, 9);

  // ---- items table ----
  const cols = [
    { key: "qty", label: "QUANTITY", x: ML, w: 18 },
    { key: "desc", label: "DESCRIPTION", x: 28, w: 92 },
    { key: "hs", label: "H.S. NUMBER", x: 120, w: 30 },
    { key: "unit", label: "UNIT PRICE", x: 150, w: 25 },
    { key: "total", label: CUR[m.currency].total, x: 175, w: 25 },
  ];
  const colXs = [ML, 28, 120, 150, 175, MRX];
  let y = leftBottom + 4;
  const drawTableHeader = () => {
    rect(ML, y, MRX - ML, 6);
    doc.setFont("helvetica", "bold"); doc.setFontSize(5.8); doc.setTextColor(70);
    cols.forEach((c) => {
      if (c.key === "unit" || c.key === "total") doc.text(c.label, c.x + c.w / 2, y + 4, { align: "center" });
      else doc.text(c.label, c.x + 1.5, y + 4);
    });
    doc.setTextColor(0);
    colXs.forEach((x) => doc.line(x, y, x, y + 6));
    y += 6;
  };
  let tableTop;
  const startTable = () => { drawTableHeader(); tableTop = y; };
  const closeTable = (minBottom) => {
    let end = Math.max(y, minBottom || 0);
    colXs.forEach((x) => doc.line(x, tableTop, x, end));
    doc.line(ML, end, MRX, end);
    return end;
  };
  startTable();
  const bodyFont = () => { doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(55); };
  items.forEach((it) => {
    const descLines = String(it.desc || "").split("\n").flatMap((l) => (l === "" ? [""] : doc.splitTextToSize(l, 88)));
    const rowH = Math.max(descLines.length * 4 + 3, 8);
    if (y + rowH > 262) { closeTable(); doc.addPage(); y = 20; startTable(); }
    bodyFont();
    const t = lineTotal(it);
    doc.text(String(it.qty || ""), ML + 1.5, y + 4.5);
    doc.text(descLines, 28 + 1.5, y + 4.5);
    doc.text(String(it.hs || ""), 120 + 1.5, y + 4.5);
    if (Number(it.unit)) doc.text(fmt(it.unit), 150 + 1.5, y + 4.5);
    if (t !== null) doc.text(fmt(t), 175 + 1.5, y + 4.5);
    doc.setTextColor(0);
    y += rowH;
  });
  if (m.countryManufacture) {
    if (y + 12 > 262) { closeTable(); doc.addPage(); y = 20; startTable(); }
    bodyFont();
    doc.text(["Country of Manufacture:", m.countryManufacture], 28 + 1.5, y + 6);
    doc.setTextColor(0);
    y += 14;
  }
  if (y + 62 > 272) { closeTable(); doc.addPage(); y = 20; startTable(); }
  const tableEnd = closeTable(Math.max(y + 6, 200));

  // ---- totals box ----
  const totRows = [
    ["SUBTOTAL", subtotal, true],
    ["HANDLING", Number(m.handling) ? Number(m.handling) : null, false],
    ["FREIGHT", Number(m.freight) ? Number(m.freight) : null, false],
    ["MISC.", Number(m.misc) ? Number(m.misc) : null, false],
  ];
  let ty = tableEnd;
  rect(120, ty, MRX - 120, 26);
  doc.line(150, ty, 150, ty + 26);
  totRows.forEach(([l, v], i) => {
    doc.setFont("helvetica", "bold"); doc.setFontSize(5.8); doc.setTextColor(70);
    doc.text(l, 122, ty + 5 + i * 6);
    doc.setTextColor(0);
    if (v !== null) {
      doc.setFont("helvetica", "bold"); doc.setFontSize(9.5); doc.setTextColor(55);
      doc.text(fmt(v), MRX - 2, ty + 5.5 + i * 6, { align: "right" });
      doc.setTextColor(0);
    }
  });
  ty += 26;
  rect(120, ty, MRX - 120, 8);
  doc.line(150, ty, 150, ty + 8);
  doc.setFont("helvetica", "bold"); doc.setFontSize(6.5); doc.setTextColor(70);
  doc.text("TOTAL", 122, ty + 5.5); doc.setTextColor(0);
  doc.setFont("helvetica", "bold"); doc.setFontSize(10.5); doc.setTextColor(40);
  doc.text(fmt(total), MRX - 2, ty + 5.8, { align: "right" });
  doc.setTextColor(0);

  // ---- certification + signature ----
  let cy = tableEnd + 4;
  doc.setFont("helvetica", "bold"); doc.setFontSize(6.5);
  doc.text(doc.splitTextToSize(String(m.certify || ""), 100), ML + 1, cy + 3);
  cy += 8;
  rect(ML, cy, 105, 21);
  if (sigFont && m.signedName) {
    doc.setFont("signature", "normal"); doc.setFontSize(15); doc.setTextColor(30);
    doc.text(m.signedName, ML + 3, cy + 7);
  }
  doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(55);
  doc.text(`${(m.signedName || "").toUpperCase()}${m.signedName && m.signedDate ? "      " : ""}${m.signedDate || ""}`, ML + 3, cy + 13);
  if (m.signedTitle) { doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.text(m.signedTitle, ML + 3, cy + 18.5); }
  doc.setTextColor(0);

  // ---- footer legal ----
  const fy = Math.max(cy + 26, ty + 12);
  doc.setFont("helvetica", "bold"); doc.setFontSize(6.2); doc.setTextColor(60);
  doc.text(`THESE COMMODITIES, TECHNOLOGY OR SOFTWARE WERE EXPORTED FROM THE UNITED STATES TO : ${m.exportedTo || ""}`, ML + 1, fy);
  doc.text("IN ACCORDANCE WITH THE EXPORT REGULATIONS.  DIVERSION CONTRARY TO U.S. LAW PROHIBITED.", ML + 1, fy + 4.5);
  doc.setTextColor(0);

  // ---- per-page header ----
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setFont("helvetica", "bold"); doc.setFontSize(13);
    doc.text("COMMERCIAL INVOICE", 105, 15, { align: "center" });
    doc.setFont("helvetica", "normal"); doc.setFontSize(9);
    doc.text(`Page No.  ${p}  of  ${pages}  Pages`, MRX, 15, { align: "right" });
  }

  return doc;
}
